import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileBarChart2, ClipboardCopy, FileSignature, Mail, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { RequirementsSummary } from "@/components/RequirementsSummary";
import { CorPricing } from "@/components/CorPricing";
import { SendReportDialog } from "@/components/SendReportDialog";
import { BidLetterDialog } from "@/components/BidLetterDialog";

const clarificationClass = (category) => {
  if (category === "standard") return "text-slate-400";
  return category?.startsWith("requirement_") ? "text-slate-300/90" : "";
};

export default function Reports() {
  const { id } = useParams();
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runId, setRunId] = useState("");
  const [busy, setBusy] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  const [letterOpen, setLetterOpen] = useState(false);

  const loadRuns = useCallback(() => api.get(`/projects/${id}/reviews`).then((r) => setRuns(r.data.filter((x) => x.status === "completed"))), [id]);
  const loadReport = useCallback(() => {
    setData(null);
    return api.get(`/projects/${id}/report`, { params: { format: "json", ...(runId ? { run_id: runId } : {}) } }).then((r) => setData(r.data));
  }, [id, runId]);
  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => { loadReport(); }, [loadReport]);

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const q = new URLSearchParams({ format: fmt, ...(runId ? { run_id: runId } : {}) });
      await downloadFile(`/projects/${id}/report?${q}`, `pm-report-${data.project.name}${runId ? "-" + data.run?.mode : ""}.${fmt}`);
      toast.success(`${fmt.toUpperCase()} report downloaded`);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const downloadCOR = async (fmt) => {
    setBusy("cor");
    try {
      await downloadFile(`/reviews/${runId}/change-order?format=${fmt}`, `change-order-request-${data.project.name}.${fmt}`);
      toast.success("Change Order Request downloaded");
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  if (!data) return <div className="p-10 text-slate-400">Building report…</div>;
  const c = data.counts;

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="reports-page">
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <div className="label-cap text-amber-500">// PM / ESTIMATOR REPORT</div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-100 mt-1 flex items-center gap-2">
            <FileBarChart2 className="w-6 h-6 text-amber-400" /> {data.project.name}
          </h1>
          <div className="text-xs font-mono text-slate-500 mt-1">{data.workspace?.name} · Generated {new Date(data.generated_at).toLocaleString()} · by {data.generated_by}</div>
        </div>
        <div className="flex gap-2 items-center">
          <select value={runId} onChange={(e) => setRunId(e.target.value)} className="bg-slate-950/60 border border-slate-800 rounded-md px-2 py-2 text-xs text-slate-100 max-w-[260px]" data-testid="report-run-select">
            <option value="">All review runs</option>
            {runs.map((r) => <option key={r.id} value={r.id}>{r.mode.replace(/_/g, " ")} · {new Date(r.created_at).toLocaleDateString()} · {r.finding_count ?? 0} findings</option>)}
          </select>
          {runId && runs.find((r) => r.id === runId)?.mode === "addendum_review" && (
            <button onClick={() => downloadCOR("docx")} disabled={!!busy} className="border border-emerald-500/60 text-emerald-300 hover:border-emerald-400 disabled:opacity-50 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2" title="Ready-to-send Change Order Request letter with quantities and citations" data-testid="export-change-order-button"><FileSignature className="w-4 h-4" /> {busy === "cor" ? "…" : "Change Order Request"}</button>
          )}
          <button onClick={() => download("pdf")} disabled={!!busy} className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-950 font-semibold px-3 py-2 rounded-md text-sm inline-flex items-center gap-2" data-testid="export-pm-report-pdf-button"><Download className="w-4 h-4" /> {busy === "pdf" ? "…" : "PDF"}</button>
          <button onClick={() => download("docx")} disabled={!!busy} className="border border-slate-700 hover:border-amber-500 disabled:opacity-50 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2" data-testid="export-pm-report-docx-button"><Download className="w-4 h-4" /> {busy === "docx" ? "…" : "DOCX"}</button>
          <button onClick={() => setLetterOpen(true)} className="border border-slate-700 hover:border-amber-500 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2" title="Ready-to-send bid clarification letter built from struck-out items and clarifications" data-testid="bid-letter-button"><FileText className="w-4 h-4" /> Bid Letter</button>
          {can("edit") && <button onClick={() => setSendOpen(true)} className="border border-slate-700 hover:border-amber-500 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2" title="Email the PDF to team members and project contacts" data-testid="email-report-button"><Mail className="w-4 h-4" /> Email</button>}
        </div>
      </div>
      <SendReportDialog projectId={id} runId={runId} corAvailable={!!runId && runs.find((r) => r.id === runId)?.mode === "addendum_review"} open={sendOpen} onOpenChange={setSendOpen} />
      <BidLetterDialog projectId={id} projectName={data.project.name} runId={runId} open={letterOpen} onOpenChange={setLetterOpen} canEdit={can("edit")} />

      <div className="sc-card p-6 mb-4">
        <div className="label-cap mb-2">Executive Summary</div>
        <p className="text-sm text-slate-300 leading-relaxed" data-testid="report-executive-summary">{data.executive_summary}</p>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-4">
        {[
          { k: "critical", label: "Critical", cls: "sev-critical" },
          { k: "high", label: "High", cls: "sev-high" },
          { k: "medium", label: "Medium", cls: "sev-medium" },
          { k: "low", label: "Low", cls: "sev-low" },
        ].map((s) => (
          <div key={s.k} className={`sc-card p-4 border ${s.cls}`}>
            <div className="label-cap">{s.label}</div>
            <div className="font-heading text-3xl font-extrabold mt-1">{c[s.k]}</div>
          </div>
        ))}
      </div>

      <Coverage items={data.coverage} />

      <RequirementsSummary projectId={id} canEdit={can("edit")} />

      {runId && runs.find((r) => r.id === runId)?.mode === "addendum_review" && <CorPricing runId={runId} canEdit={can("edit")} />}

      {[
        { key: "critical_risks", title: "Critical Risks" },
        { key: "scope_gaps", title: "Scope Gaps & Coordination Items" },
        { key: "contract_risks", title: "Contract Risks" },
        { key: "specification_risks", title: "Specification Findings" },
        { key: "aisc_compliance", title: "AISC / AWS Compliance" },
        { key: "spec_vs_drawings", title: "Spec vs. Drawings Cross-Check" },
        { key: "addenda", title: "Addenda & Change Impacts" },
        { key: "custom", title: "Custom Findings" },
      ].map((s) => (
        <ReportSection key={s.key} title={s.title} items={data[s.key]} />
      ))}

      {data.exclusions?.length > 0 && (
        <div className="sc-card p-6 mb-4" data-testid="report-section-exclusions">
          <div className="label-cap mb-3">Standard Exclusions & Clarifications ({data.exclusions.length})</div>
          <ol className="text-xs text-slate-300 space-y-1 list-decimal pl-5">
            {data.exclusions.map((e) => <li key={`${e.playbook}:${e.text}`}>{e.text} <span className="text-slate-600 font-mono">· {e.playbook}</span></li>)}
          </ol>
          {data.exclusion_conflicts?.length > 0 && (
            <div className="mt-3 text-xs text-red-400" data-testid="report-exclusion-conflicts">
              <div className="label-cap text-red-400 mb-1">Documents conflicting with exclusions</div>
              {data.exclusion_conflicts.map((f) => <div key={f.id}>• {f.title} — {f.source_section} (p.{f.source_page})</div>)}
            </div>
          )}
        </div>
      )}

      {data.clarifications?.length > 0 && (
        <div className="sc-card p-6 mb-4" data-testid="report-section-clarifications">
          <div className="flex items-center justify-between mb-3">
            <div className="label-cap">Appendix C — Bid Clarifications ({data.clarifications.length})</div>
            <button onClick={() => { navigator.clipboard.writeText(data.clarifications.map((c) => `${c.n}. ${c.text}`).join("\n")); toast.success("Clarifications copied"); }}
              className="text-xs border border-slate-700 hover:border-amber-500 px-2.5 py-1.5 rounded-md inline-flex items-center gap-1.5" data-testid="copy-clarifications-button"><ClipboardCopy className="w-3.5 h-3.5" /> Copy all</button>
          </div>
          <ol className="text-xs text-slate-300 space-y-1.5 list-decimal pl-5">
            {data.clarifications.map((c) => (
              <li key={c.n} className={clarificationClass(c.category)} data-testid={`clarification-${c.n}`}>
                {c.text} <span className="text-slate-600 font-mono">[{c.cite}]</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {data.rfis?.length > 0 && (
        <div className="sc-card p-6 mb-4" data-testid="report-section-draft-rfis">
          <div className="label-cap mb-3">Appendix A — Draft RFIs ({data.rfis.length})</div>
          <div className="space-y-3">
            {data.rfis.map((f, i) => (
              <div key={f.id} className="border-l-2 border-slate-700 pl-3">
                <div className="text-xs font-mono text-amber-400">RFI-{String(i + 1).padStart(3, "0")} · p.{f.source_page} {f.source_section}</div>
                <div className={`font-heading text-sm font-semibold ${f.excluded_by ? "text-slate-500 line-through decoration-red-500/70" : "text-slate-100"}`}>{f.title}{f.excluded_by && <span className="ml-2 text-[10px] font-mono uppercase text-red-400 border border-red-500/40 rounded px-1 no-underline" title={f.excluded_by}>excluded per playbook</span>}</div>
                <div className="text-xs text-slate-300 mt-1 whitespace-pre-wrap">{f.draft_rfi}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="sc-card p-6 mt-4">
        <div className="label-cap mb-3">Appendix B — Source Citation Index</div>
        <ul className="text-xs font-mono space-y-1 max-h-96 overflow-y-auto">
          {data.findings.map((f) => (
            <li key={f.id} className="text-slate-400 border-l border-slate-700 pl-2">
              [{f.severity?.toUpperCase()}] {f.csi_division} · p.{f.source_page} {f.source_section} — <span className="text-slate-100">{f.title}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Coverage({ items }) {
  if (!items?.length) return null;
  return (
    <div className="sc-card p-6 mb-4" data-testid="report-section-coverage">
      <div className="label-cap mb-3">Document Coverage — CSI Division 05 / 10</div>
      <div className="space-y-3">
        {items.map((d) => (
          <div key={d.name}>
            <div className="text-sm text-slate-100 font-semibold">{d.name} <span className="text-xs text-slate-500 font-mono ml-2">divisions: {d.divisions?.join(", ") || "n/a"}</span></div>
            <div className="grid grid-cols-2 gap-x-6 text-xs font-mono mt-1">
              {d.steel_sections.map((s) => <div key={s.code} className="text-slate-300"><span className="text-amber-400">{s.code}</span> {s.title} <span className="text-slate-500">pp. {s.page_start}–{s.page_end}</span></div>)}
              {d.toc_missing_body.map((m) => <div key={m.code} className="text-red-400">{m.code} {m.title} — in TOC, body not found</div>)}
              {!d.steel_sections.length && !d.toc_missing_body.length && <div className="text-slate-500">No Division 05/10 sections detected</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportSection({ title, items }) {
  if (!items?.length) return null;
  return (
    <div className="sc-card p-6 mb-4" data-testid={`report-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="label-cap mb-3">{title} ({items.length})</div>
      <div className="space-y-3">
        {items.map((f) => (
          <div key={f.id} className={`border-l-2 pl-3 ${f.excluded_by ? "border-red-500/40 opacity-70" : "border-amber-500/50"}`} data-testid={f.excluded_by ? `report-finding-excluded-${f.id}` : undefined}>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold sev-${f.severity}`}>{f.severity?.toUpperCase()}</span>
              <span className={`font-heading text-sm font-semibold ${f.excluded_by ? "text-slate-500 line-through decoration-red-500/70" : "text-slate-100"}`}>{f.title}</span>
              {f.excluded_by && <span className="text-[10px] font-mono uppercase text-red-400 border border-red-500/40 rounded px-1" title={f.excluded_by}>excluded per playbook</span>}
              <span className="ml-auto text-xs font-mono text-slate-500">{f.csi_division} · p.{f.source_page} {f.source_section}{f.drawing_ref ? ` · ⌗ ${f.drawing_ref}` : ""}{f.mode === "addendum_review" ? ` · Δ ${String(f.change_type || "").replace(/_/g, " ")} · cost ${f.cost_impact || "?"}` : ""}</span>
            </div>
            {f.relevant_language && <div className="text-xs italic text-slate-400 font-mono">“{f.relevant_language}”</div>}
            {f.why_it_matters && <div className="text-xs mt-1 text-slate-300"><span className="text-amber-400 font-semibold">Impact:</span> {f.why_it_matters}</div>}
            {f.recommended_action && <div className="text-xs mt-0.5 text-slate-300"><span className="text-amber-400 font-semibold">Action:</span> {f.recommended_action}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
